import { useRef, useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { InboundLayout, InboundHeaderCard } from "ui-components/Layout";
import {
  InboundSaveButton,
  InboundSubmitButton,
  InboundDetailAddButton,
  InboundDetailEditButton,
} from "ui-components/Form";

import { headerRender, headerValues, headerValidation } from "./po-receiving-schema";
import { detailRender, detailValues, detailValidation } from "./po-receiving-details-schema";

import { Success, Error_, InfoDialog, LoadingDialog } from "ui-components/Alert";
import { skuValidation } from "layouts/views/common/validation";
import { dateValidation } from "layouts/views/common/validation";
import { useMutation, useQuery } from "@tanstack/react-query";
import POReceiving from "./components/POReceiving";
import POReceivingDetail from "./components/POReceivingDetail";
import { poRecvColumns } from "./columns";
import { useLocation, useNavigate } from "react-router-dom";
import { getPOReceiving, editPOReceiving } from "services/data.inbound.service";
import BasicTable2 from "ui-components/tables/BasicTable2";
import {
  cancelDetail,
  deleteDetail,
  getSplitDetailsTotalQuantity,
  getTotalQuantity,
  updateDetail,
  serialValidation_,
} from "../common/common";
import { PORecvCaption } from "../components/Caption";
import ApplyAll from "../components/ApplyAll";
import { statusLabel } from "layouts/views/common/enum";
import { statusObj } from "layouts/views/common/enum";
import POReceivingDivisionGroup from "./components/POReceivingDivisionGroup";
import { getShowDivision } from "services/data.inbound.service";

function EditPOReceiving() {
  const location = useLocation();
  const navigate = useNavigate();
  const { recvCode, customerCode } = location.state;
  const [formValues, setFormValues] = useState(headerValues);
  const [alert, setAlert] = useState(null);
  const [details, setDetails] = useState([]);
  const [uploadData, setUploadData] = useState([]);
  const [showDivision, setShowDivision] = useState(false);
  const onError = (data) => {
    setAlert(<Error_ data={data} onChange={setAlert} />);
  };
  const editPOReceivingMutation = useMutation(editPOReceiving, {});

  const { isFetching } = useQuery({
    queryKey: ["po-recv", { recvCode, customerCode }],
    queryFn: () => getPOReceiving({ recvCode, customerCode }),
    onSuccess: (data) => {
      const { status, poRecvDetails, recvType, divisionQuantity, ...poRecvValues } = data.data.data;
      setUploadData(divisionQuantity);
      setFormValues({
        ...poRecvValues,
        status: statusLabel[status],
        recvType: "Standard Receipt (Normal)",
      });
      setDetails(
        poRecvDetails.map((detail) => ({ ...detail, status: statusLabel[detail.status] }))
      );
    },
    retry:1,
    onError
  });
  useQuery({
    queryKey: ["show-division", customerCode],
    queryFn: () => getShowDivision({ customerCode }),
    enabled: Boolean(customerCode),
    onSuccess: (data) => setShowDivision(data.data.data),
    onError,
  });
  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(headerValidation),
  });
  const {
    control,
    watch,
    formState: { errors, isSubmitting },
    setValue,
    getValues,
    trigger,
  } = form;
  const headerProps = { headerRender, customerCode, control, setValue, errors, mode: "EDIT" };

  const [vendorCode] = watch(["vendorCode"]);

  const detailProps = { details, setDetails,uploadData,
    setUploadData,
    showDivision, values: { customerCode, vendorCode } };

  const onSubmit = async () => {
    window.scrollTo(0, 0);
    const isValid = await trigger();
    if (!isValid) return;

    if (details.length === 0) {
      setAlert(<Error_ content="Invalid PO Receiving Details" onChange={setAlert} />);
      return;
    }

    const serialNumbersMap = new Map();
    const affectedDets = new Set();

    for (const det of details) {
      for (const serial of det.serialNumbers) {
        const { serialNumber } = serial;
        if (serialNumbersMap.has(serialNumber)) {
          affectedDets.add(det.lineNo);
          affectedDets.add(serialNumbersMap.get(serialNumber).lineNo);
        } else {
          serialNumbersMap.set(serialNumber, det);
        }
      }
    }
    const result = Array.from(affectedDets);
    if (result.length > 0) {
      setAlert(
        <Error_
          content={`Duplicate Serial Numbers in LineNo ${result.join(", ")}`}
          onChange={setAlert}
        />
      );
      return;
    }

    const notTally = details.filter(x => x.inbSerialNbrCtrl && x.quantity !== x.serialNumbers.length);
    if (notTally.length > 0) {
      setAlert(
        <Error_
          content={`Serial Numbers Count Not Same as Quantity in LineNo ${notTally.map(x => { return x.lineNo }).join(", ")}`}
          onChange={setAlert}
        />
      );
      return;
    }

    const data = getValues();

    const req = {
      ...data,
      divisionQuantity: JSON.stringify(uploadData),
      poRecvDetails: details.map((d) => ({ ...d, status: statusObj[d.status], uom: d.uom.label })),
    };

    editPOReceivingMutation.mutate(
      { recvCode, customerCode, req },
      {
        onSuccess: (data) => navigate("/inbound/po-receiving", { state: data.data.message }),
        onError,
      }
    );
  };
  return (
    <InboundLayout lg={12}>
      {isFetching && <LoadingDialog />}
      {alert}
      <InboundHeaderCard>
        <form noValidate>
          <POReceiving {...headerProps} />
        </form>
      </InboundHeaderCard>

      <Detail {...detailProps} />
      <InboundSaveButton url="/inbound/po-receiving" onClick={onSubmit} disabled={isSubmitting} />
    </InboundLayout>
  );
}

const Detail = ({ details, setDetails, uploadData, setUploadData, showDivision, values }) => {
  const columns = [
    {
      field: "actions",
      headerName: "Action",
      renderActions: ["Add", "Cancel"],
      // getActions: (params) => {
      //   const { row } = params;
      //   const cellItem = row.newItem ? (
      //     <DelAction
      //       onClick={() => {
      //         handleDelete(params.row);
      //       }}
      //     />
      //   ) : (
      //     <>
      //       <CancelAction
      //         onClick={() => handleCancel(params.row)}
      //         disabled={params.row.status === "Cancel" ? true : false}
      //       />
      //       {params.row.status === "Open" && (
      //         <AddAction
      //           onClick={() => {
      //             handleAdd(params.row);
      //           }}
      //         />
      //       )}
      //     </>
      //   );
      //   return [cellItem];
      // },
      width: 80,
    },
    ...poRecvColumns,
  ];
  const [alert, setAlert] = useState(null);
  const [serialAlert, setSerialAlert] = useState(null);
  const [formValues, setFormValues] = useState(null);
  const [dialog, setDialog] = useState(null);
  const doRef = useRef(null);
  const invRef = useRef(null);
  const lineNo = formValues ? formValues.lineNo : details.length + 1;

  const form = useForm({
    defaultValues: { ...detailValues, lineNo },
    values: formValues || {
      ...detailValues,
      lineNo,
      status: "",
      vDONo: doRef.current,
      vInvNo: invRef.current,
    },
    resolver: yupResolver(detailValidation),
  });
  const {
    control,
    watch,
    formState: { errors, isDirty, isSubmitting, dirtyFields },
    setValue,
    getValues,
    handleSubmit,
    reset,
  } = form;
  const [skuCode, recvWh, recvZone] = watch([
    "skuCode",
    "recvWh",
    "recvZone",
  ]);

  const onSubmit = (data) => {
    setSerialAlert(null);
    if (Boolean(dirtyFields.serialNumbers)) {
      if (data.inbSerialNbrCtrl && data.quantity !== data.serialNumbers.length) {
        const errorMsg =
          data.quantity > data.serialNumbers.length
            ? `Not Enough Serial Number(${data.serialNumbers.length})`
            : `Too Many Serial Number(${data.serialNumbers.length})`;
        setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
        return;
      }
      const isSerialAnyDuplicate = serialValidation_(details, data);
      if (isSerialAnyDuplicate.isValid) {
        if (isSerialAnyDuplicate.type === "Single") {
          const errorMsg = "Duplicate Serial Number " + isSerialAnyDuplicate.serialNumbers.join(", ");
          setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
          return;
        } else if (isSerialAnyDuplicate.type === "Multiple") {
          const errorMsg =
            "Duplicate Serial Number " +
            isSerialAnyDuplicate.serialNumbers.join(", ") +
            " In LineNo " +
            isSerialAnyDuplicate.lineNos.join(", ");
          setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
          return;
        }
      }
    }
    doRef.current = data.vDONo;
    invRef.current = data.vInvNo;
    setDetails(updateDetail(details, data));
    setFormValues(null);
    setAlert(<Success content="Updated" onChange={setAlert} />);
  };
  const onRowClick = (v) => {
    setSerialAlert(null);
    //const { id, ...values } = v.row;
    const values = v;
    if (formValues !== null && isDirty) {
      const data = getValues();
      if (Boolean(dirtyFields.serialNumbers)) {
        if (data.inbSerialNbrCtrl && data.quantity !== data.serialNumbers.length) {
          const errorMsg =
            data.quantity > data.serialNumbers.length
              ? `Not Enough Serial Number(${data.serialNumbers.length})`
              : `Too Many Serial Number(${data.serialNumbers.length})`;
          setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
          return false;
        }
        const isSerialAnyDuplicate = serialValidation_(details, data);
        if (isSerialAnyDuplicate.isValid) {
          if (isSerialAnyDuplicate.type === "Single") {
            const errorMsg =
              "Duplicate Serial Number " + isSerialAnyDuplicate.serialNumbers.join(", ");
            setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
            return false;
          } else if (isSerialAnyDuplicate.type === "Multiple") {
            const errorMsg =
              "Duplicate Serial Number " +
              isSerialAnyDuplicate.serialNumbers.join(", ") +
              " In LineNo " +
              isSerialAnyDuplicate.lineNos.join(", ");
            setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
            return false;
          }
        }
      }
      setDialog(
        <InfoDialog
          title={`Unsaved changes for Line No ${formValues.lineNo}`}
          content="Do you want to save the changes"
          onOk={() => {
            setDetails(updateDetail(details, data));
            setDialog(null);
            setFormValues(values);
          }}
          onClose={() => {
            setDialog(null);
            setFormValues(values);
          }}
        />
      );
    } else {
      setFormValues(values);
    }
    return true;
  };
  const onAdd = (v) => {
    const { id, ...values } = v;
    setDetails([...details, { ...values, lineNo: details.length + 1, newItem: true }]);
    setAlert(<Success content="Added" onChange={setAlert} />);
  };
  const onCancel = (data) => {
    setDetails(cancelDetail(details, data));
    setFormValues(null);
    setAlert(<Success content="Cancelled" onChange={setAlert} />);
  };
  const onDelete = (data) => {
    if (data.newItem !== true) return;
    setDetails(deleteDetail(details, data));
    setFormValues(null);
    setAlert(<Success content="Deleted" onChange={setAlert} />);
  };

  const detailProps = {
    detailRender,
    values: { ...values, skuCode, recvWh, recvZone },
    control,
    errors,
    setValue,
    getValues,
    mode: "EDIT",
    serialAlert
  };
  const totalQuantity = getTotalQuantity(details, "quantity");
  const totalExpectedQuantity = getSplitDetailsTotalQuantity(details, "qtyExpected", "poLineNo");
  const caption = (
    <PORecvCaption
      totalRows={details.length}
      totalQuantity={totalQuantity}
      totalExpectedQuantity={totalExpectedQuantity}
    />
  );
  const tableProps = {
    columns,
    rows: details,
    caption,
    onAdd,
    onDelete,
    onCancel,
    onRowClick,
    mode: "EDIT",
  };
  const applyAllProps = { formValues, setFormValues, setValue, details, setDetails };
  return (
    <>
    <InboundHeaderCard>
      {dialog}
      {alert}
      <ApplyAll {...applyAllProps} />

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <POReceivingDetail {...detailProps} />
        {formValues !== null && (
          <InboundDetailEditButton disabled={!isDirty} onClick={() => setFormValues(null)} />
        )}
      </form>

      <BasicTable2 {...tableProps} />
    </InboundHeaderCard>
    {showDivision && (
        <POReceivingDivisionGroup
          details={details}
          uploadData={uploadData}
          setUploadData={setUploadData}
        />
      )}
      </>
  );
};

export default EditPOReceiving;
